import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Spinner from "@/components/Spinner";
import useAddCollectionOrder from "@/hooks/collectionOrder/useAddCollectionOrder";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { formatNumberWithDotAndComma } from "@/utils/moneyFormatter";
import WilayaDropdown from "@/components/WilayaDropdown";
import CommuneDropdown from "@/components/CommuneDropdown";
import TimePicker from "../addCollectionOrder/TimePicker";
import { CustomerDropdown } from "../addCollectionOrder/CustomerDropdown";
import { useUpdateCollectionOrder } from "@/hooks/collectionOrder/useUpdateCollectionOrder";
import { useForm } from "react-hook-form";
import CollectorDropDownUpdate from "./CollectorDropDownUpdate";

type PropsAddEmployee = {
  collectionId: string;
  onAddCollectionOrder: () => void;
  isMobile?: boolean;
};

const CollectionUpdate: React.FC<PropsAddEmployee> = ({
  collectionId,
  onAddCollectionOrder,
  isMobile = false,
}) => {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState<Client>({} as Client);
  const [collector, setCollector] = useState<Employee>({} as Employee);
  const [time, setTime] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState("");
  const [amount, setAmount] = useState(0);
  const [selectedWilaya, setSelectedWilaya] = useState<string>("");

  const [isPhoneEdited, setIsPhoneEdited] = useState(false);
  const [isLocationEdited, setIsLocationEdited] = useState(false);
  const [isTotalBalanceEdited, setIsTotalBalanceEdited] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAmountBlur = () => {
    const formattedValue = formatNumberWithDotAndComma(amount.toString());
    setAmount(amount);
    form.setValue("amount", amount);
  };

  const { defaultValues, loading, error, updateCollection } =
    useUpdateCollectionOrder(collectionId || "");

  const form = useForm({
    defaultValues, // Set default values in the form
    mode: "onChange",
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
      setCollector(defaultValues.Collector);
    }
  }, [defaultValues, form]);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      await updateCollection(values);
      setOpen(false);
      toast.success("Client updated successfully!");
    } catch (error) {
      toast.error("Failed to update client.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prefill phone and location only if the user has not edited the phone field
  useEffect(() => {
    if (!isPhoneEdited && customer?.phone) {
      form.setValue("phone", customer.phone);
    }
    if (!isLocationEdited && customer?.positionGps) {
      form.setValue("location", customer.positionGps);
    }

    if (customer && customer.wilaya) {
      form.setValue("wilaya", customer.wilaya);
    }

    if (customer && customer.commune) {
      form.setValue("commune", customer.commune);
    }

    if (!isTotalBalanceEdited && customer?.totalBalance) {
      form.setValue("totalBalance", customer.totalBalance);
    }
  }, [customer, isPhoneEdited, isLocationEdited, isTotalBalanceEdited, form]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Phone changed, setting isPhoneEdited to true");
    setIsPhoneEdited(true); // Mark as edited when the user types
    form.setValue("phone", e.target.value);
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Location changed, setting isLocationEdited to true");
    setIsLocationEdited(true); // Mark as edited when the user types
    form.setValue("location", e.target.value);
  };

  const handleTotalBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Total balance changed, setting isTotalBalanceEdited to true");
    setIsTotalBalanceEdited(true); // Mark as edited when the user types
    form.setValue("totalBalance", e.target.value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="h-7 w-7 border flex justify-center items-center border-primary/90 rounded-lg hover:bg-primary/10 transition ease-in-out duration-200">
        <img
          className="h-[17px] w-[17px]"
          src="/assets/icons/edit.svg"
          alt="edit"
        />
      </DialogTrigger>
      <DialogContent className="md:max-w-3xl p-8">
        <DialogHeader>
          <DialogTitle className="font-medium text-[18px]">
            Update Collection order
          </DialogTitle>
          <DialogDescription>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="flex flex-col mt-4 gap-4 h-[calc(100vh-200px)] md:h-full text-black overflow-y-scroll"
              >
                {/* CUSTOMER AND COLLECTOR */}
                <div className="flex flex-col md:flex-row md:gap-8 gap-4">
                  <div className="md:w-1/2">
                    <CustomerDropdown
                      isUpdate={true}
                      addCommandeForm={form}
                      setCustomer={setCustomer}
                    />
                  </div>
                  <div className="md:w-1/2">
                    <CollectorDropDownUpdate
                      value={collector.fullname || ""}
                      collector={collector}
                      onValueChange={(value: string) => {
                        const parsedObject = JSON.parse(value); // *parse the object here

                        form.setValue("collectorId", +parsedObject.id);
                        setCollector(parsedObject);
                      }}
                    />
                  </div>
                </div>

                {/* wilaya and date */}
                <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                  <FormField
                    control={form.control}
                    name="wilaya"
                    render={({ field }) => (
                      <FormItem className="md:w-1/2">
                        <FormControl>
                          <WilayaDropdown
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedWilaya(field.value);
                            }}
                            value={field.value || customer?.wilaya || ""}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col md:w-1/2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "bg-white pr-2 h-[44px] border-muted-foreground text-black w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto  h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date("1900-01-01")}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* total balance and amount */}
                <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                  <FormField
                    control={form.control}
                    name="amount"
                    rules={{ required: "Amount is required" }}
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          <div className="relative">
                            <p className="absolute top-[25%] right-2 text-primary">
                              Amount to collect
                            </p>
                            <Input
                              className="pr-28"
                              placeholder="00,00 DZD"
                              value={amount}
                              onChange={(e) =>
                                setAmount(Number(e.target.value))
                              } // Keep track of user input
                              onBlur={handleAmountBlur} // Format when input loses focus
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="justify-end flex p-0 text-muted-foreground"
                  onClick={() => setShowMoreInfo(!showMoreInfo)}
                >
                  {showMoreInfo ? (
                    <>
                      Hide additional info
                      <ChevronUpIcon className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show additional info
                      <ChevronDownIcon className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                {showMoreInfo && (
                  <div className="flex flex-col mt-4 gap-4">
                    <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                      <FormField
                        control={form.control}
                        name="totalBalance"
                        // rules={{ required: 'Total Balance is required' }}
                        render={({ field }) => (
                          <FormItem className="md:w-1/2">
                            <FormControl>
                              <div className="relative">
                                <p className="absolute top-[25%] left-2 text-primary">
                                  Total Balance
                                </p>
                                <Input
                                  className="pl-28"
                                  {...field}
                                  value={
                                    isTotalBalanceEdited
                                      ? field.value
                                      : customer?.totalBalance || ""
                                  } // Use field.value if phone is edited
                                  onChange={handleTotalBalanceChange} // Track input changes and mark as edited
                                  placeholder="00,00 DZD"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="commune"
                        render={({ field }) => (
                          <FormItem className="md:w-1/2">
                            <FormControl>
                              <CommuneDropdown
                                wilaya={form.getValues("wilaya")}
                                onValueChange={field.onChange}
                                defaultValue={
                                  field.value || customer?.commune || ""
                                }
                                placeholder="Commune"
                              />
                            </FormControl>

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                      <TimePicker
                        value={form.getValues("hour")}
                        onValueChange={(value: string) => {
                          setTime(value);
                          form.setValue("hour", value);
                        }}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        rules={{ required: "Phone is required" }}
                        render={({ field }) => (
                          <FormItem className="md:w-1/2">
                            <FormControl>
                              <Input
                                {...field}
                                value={
                                  isPhoneEdited
                                    ? field.value
                                    : customer?.phone || ""
                                } // Use field.value if phone is edited
                                onChange={handlePhoneChange} // Track input changes and mark as edited
                                placeholder="Phone number"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex flex-col md:flex-row md:gap-8 gap-4">
                      <FormField
                        control={form.control}
                        name="location"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <FormControl>
                              <Input
                                {...field}
                                value={
                                  isLocationEdited
                                    ? field.value
                                    : customer?.positionGps || ""
                                }
                                onChange={handleLocationChange}
                                placeholder="33.3384687308, 67.87692730287"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                <DialogFooter className="mt-4 flex w-full">
                  <div className="mx-auto mt-8 mb-6">
                    <Button
                      disabled={isSubmitting}
                      className="px-20 "
                      type="submit"
                    >
                      {isSubmitting ? <Spinner /> : <p>Save</p>}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </Form>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionUpdate;
